import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { FileText, Plus, Search } from 'lucide-react';
import { api } from '../../api';
import { pode, useSessao } from '../../sessao';
import { Modal } from '../../componentes/Modal';
import { EscolherLocal, type Escolha } from '../chamados/EscolherLocal';
import { Servicos } from './Servicos';
import { NOME_STATUS_ORC, classeStatusOrc, dataBr, real, type Orcamento } from './comum';

const FILTROS = [['abertos', 'Em aberto'], ['rascunho', 'A revisar'], ['enviado', 'Enviados'], ['aprovado', 'Aprovados'], ['recusado', 'Recusados'], ['', 'Todos']] as const;

export function ListaOrcamentos() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') ?? 'orcamentos';
  const filtro = params.get('status') ?? 'abertos';
  const [busca, setBusca] = useState(params.get('busca') ?? '');
  const [lista, setLista] = useState<Orcamento[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const podeEditar = pode(usuario, 'orcamentos.editar');

  useEffect(() => {
    if (aba !== 'orcamentos') return;
    const q = new URLSearchParams();
    if (filtro) q.set('status', filtro);
    if (params.get('busca')) q.set('busca', params.get('busca')!);
    api<Orcamento[]>(`/orcamentos?${q}`).then(setLista).catch((e) => setErro(e.message));
  }, [params, filtro, aba]);
  useEffect(() => {
    const t = setTimeout(() => { if ((params.get('busca') ?? '') !== busca) { const p = new URLSearchParams(params); busca ? p.set('busca', busca) : p.delete('busca'); setParams(p, { replace: true }); } }, 300);
    return () => clearTimeout(t);
  }, [busca]); // eslint-disable-line react-hooks/exhaustive-deps
  const setar = (k: string, v: string) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const totalAberto = lista?.filter((o) => o.status === 'enviado').reduce((a, o) => a + Number(o.total), 0) ?? 0;

  return (
    <>
      <div className="cabecalho-pagina">
        <div><span className="kicker">Comercial</span><h1 className="titulo">Orçamentos</h1></div>
        {aba === 'orcamentos' && podeEditar && <button className="btn" onClick={() => setNovo(true)}><Plus size={18} aria-hidden /> Novo orçamento</button>}
      </div>
      <div className="segmentos" role="tablist" style={{ marginBottom: 16 }}>
        <button className={aba === 'orcamentos' ? 'ativo' : ''} onClick={() => setar('aba', '')}>Orçamentos</button>
        <button className={aba === 'servicos' ? 'ativo' : ''} onClick={() => setar('aba', 'servicos')}>Catálogo de serviços</button>
      </div>
      {aba === 'servicos' ? <Servicos /> : (
        <>
          <div className="barra-filtros">
            <label className="campo-busca"><Search size={18} strokeWidth={1.7} aria-hidden />
              <input type="search" placeholder="Número, cliente, objeto ou valor" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar orçamentos" />
            </label>
            <div className="segmentos" role="group" aria-label="Situação">
              {FILTROS.map(([v, r]) => <button key={v} className={filtro === v ? 'ativo' : ''} onClick={() => setar('status', v || 'todos')}>{r}</button>)}
            </div>
          </div>
          {erro && <p className="alerta" role="alert">{erro}</p>}
          {filtro === 'abertos' && totalAberto > 0 && <p className="nota">{real(totalAberto)} em orçamentos enviados aguardando o cliente.</p>}
          {lista && lista.length === 0 && <div className="cartao vazio"><div className="icone-tile"><FileText size={24} strokeWidth={1.7} aria-hidden /></div><p>Nenhum orçamento com esse filtro.</p></div>}
          {lista && lista.length > 0 && (
            <div className="cartao tabela-cartao rolagem-x">
              <table className="tabela">
                <thead><tr><th>Orçamento</th><th>Cliente</th><th>Situação</th><th>Origem</th><th className="num">Total</th><th>Validade</th></tr></thead>
                <tbody>
                  {lista.map((o) => (
                    <tr key={o.id} onClick={() => navegar(`/orcamentos/${o.id}`)}>
                      <td><Link to={`/orcamentos/${o.id}`} onClick={(e) => e.stopPropagation()}><strong className="mono">{o.numero}</strong></Link><small>{o.titulo ?? ''}</small></td>
                      <td>{o.cliente}<small>{o.local ?? ''}</small></td>
                      <td><span className={`situacao ${classeStatusOrc(o.status)}`}>{NOME_STATUS_ORC[o.status]}</span></td>
                      <td>{o.os_numero ? `OS ${o.os_numero}` : 'Escritório'}<small>{o.criado_por ?? ''}</small></td>
                      <td className="num">{real(o.total)}</td>
                      <td>{dataBr(o.validade)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <Modal titulo="Novo orçamento" aberto={novo} aoFechar={() => setNovo(false)}>
        {novo && <NovoOrcamento aoCriar={(id) => { setNovo(false); navegar(`/orcamentos/${id}`); }} aoCancelar={() => setNovo(false)} />}
      </Modal>
    </>
  );
}

function NovoOrcamento({ aoCriar, aoCancelar }: { aoCriar: (id: string) => void; aoCancelar: () => void }) {
  const [onde, setOnde] = useState<Escolha>({ cliente_id: null, cliente: null, local_id: null });
  const [titulo, setTitulo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  async function criar() {
    setErro(null);
    try { const r = await api<{ id: string }>('/orcamentos', { metodo: 'POST', corpo: { cliente_id: onde.cliente_id, local_id: onde.local_id, titulo: titulo.trim() || null } }); aoCriar(r.id); }
    catch (e) { setErro(e instanceof Error ? e.message : 'Não foi possível criar.'); }
  }
  return (
    <div className="form">
      <EscolherLocal valor={onde} aoMudar={setOnde} />
      <label>Objeto<input value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={200} placeholder='Ex.: "Troca do motor do portão de veículos"' /></label>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      <div className="acoes-form">
        <button type="button" className="btn-fantasma" onClick={aoCancelar}>Cancelar</button>
        <button className="btn" disabled={!onde.cliente_id} onClick={criar}>Criar e montar itens</button>
      </div>
    </div>
  );
}
